import React, { useEffect, useState } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';

const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_DAYS = 14; // erneut zeigen nach 14 Tagen

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const InstallPWABanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // bereits installiert

    // Wurde vor weniger als DISMISS_DAYS dismissed?
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const daysAgo = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
      if (daysAgo < DISMISS_DAYS) return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // 30 Sekunden warten, damit Banner nicht aufdringlich erscheint
      setTimeout(() => setShowBanner(true), 30000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari unterstützt beforeinstallprompt NICHT → Hinweis nach 45 Sek.
    if (isIOS()) {
      const t = setTimeout(() => setShowBanner(true), 45000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', handler);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setShowBanner(false);
    setShowIosHint(false);
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
        setDeferredPrompt(null);
      } else {
        dismiss();
      }
    } else if (isIOS()) {
      setShowIosHint(true);
    }
  };

  if (!showBanner && !showIosHint) return null;

  if (showIosHint) {
    return (
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={dismiss}>
        <div
          className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          data-testid="pwa-ios-instructions"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[hsl(var(--primary))] text-white flex items-center justify-center font-bold">ZO</div>
              <h3 className="font-semibold text-lg">Zum Startbildschirm hinzufügen</h3>
            </div>
            <button onClick={dismiss} aria-label="Schliessen" className="p-1 -mr-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
          <ol className="space-y-3 text-sm text-[hsl(var(--muted-foreground))]">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))] flex items-center justify-center font-semibold text-xs">1</span>
              <span className="pt-1">Tippe auf das <strong>Teilen-Symbol</strong> <Share className="inline w-4 h-4 mx-1" /> in der Safari-Leiste.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))] flex items-center justify-center font-semibold text-xs">2</span>
              <span className="pt-1">Scrolle nach unten und wähle <strong>"Zum Home-Bildschirm"</strong> <Plus className="inline w-4 h-4 mx-1" />.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))] flex items-center justify-center font-semibold text-xs">3</span>
              <span className="pt-1">Bestätige mit <strong>"Hinzufügen"</strong>.</span>
            </li>
          </ol>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-4 sm:w-80 z-[90] bg-white rounded-2xl shadow-2xl border border-[hsl(var(--border))] p-4 animate-in slide-in-from-bottom-4 fade-in duration-300"
      role="region"
      aria-label="App installieren"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-[hsl(var(--primary))] text-white flex items-center justify-center font-bold flex-shrink-0">ZO</div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">App installieren</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
            Schnellzugriff vom Home-Bildschirm – wie eine native App.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={install}
              className="flex-1 bg-[hsl(var(--primary))] text-white text-xs font-semibold py-2 px-3 rounded-lg hover:bg-[hsl(var(--primary)/0.9)] flex items-center justify-center gap-1"
              data-testid="pwa-install-btn"
            >
              <Download className="w-3.5 h-3.5" />
              Installieren
            </button>
            <button
              onClick={dismiss}
              className="text-xs text-[hsl(var(--muted-foreground))] py-2 px-3 hover:bg-gray-100 rounded-lg"
              data-testid="pwa-dismiss-btn"
            >
              Später
            </button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="Schliessen" className="p-1 -mr-1 -mt-1 hover:bg-gray-100 rounded">
          <X className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
        </button>
      </div>
    </div>
  );
};

export default InstallPWABanner;
